import { ArrowDownLeft, ArrowUpRight, Users, AlertTriangle, Loader2, Activity } from 'lucide-react';
import { useDateFormatter } from '@/shared/hooks';
import { useGuardDashboard } from '../hooks';
import { ShiftControls, PanicButton } from '../components';

export const GuardDashboardPage = () => {
  const { dashboard, loading, error } = useGuardDashboard();
  const { formatTime } = useDateFormatter();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-xl">
        {error}
      </div>
    );
  }

  const stats = dashboard?.todayStats;

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-800">Panel de Guardia</h2>
        <p className="text-slate-500 text-sm">Resumen del dia y acciones rapidas</p>
      </div>

      {/* Panic Button - Prominent */}
      <PanicButton />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Shift Controls */}
          <ShiftControls />
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Today's Stats */}
          {stats && (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownLeft className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-slate-600">Accesos</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{stats.totalAccesses}</p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpRight className="w-5 h-5 text-blue-500" />
                  <span className="text-sm text-slate-600">Residentes</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{stats.residentAccesses}</p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-purple-500" />
                  <span className="text-sm text-slate-600">Visitantes</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{stats.guestAccesses}</p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  <span className="text-sm text-slate-600">Denegados</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{stats.deniedAccesses}</p>
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {dashboard?.recentActivity && dashboard.recentActivity.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-primary-500" />
                <h3 className="font-semibold text-slate-800">Actividad Reciente</h3>
              </div>

              <div className="space-y-3 max-h-64 overflow-y-auto">
                {dashboard.recentActivity.map((activity, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0 last:pb-0"
                  >
                    <div className="w-2 h-2 mt-2 bg-primary-500 rounded-full" />
                    <div className="flex-1">
                      <p className="text-sm text-slate-700">{activity.description}</p>
                      <p className="text-xs text-slate-400">
                        {formatTime(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Alerts */}
          {dashboard?.activeAlerts && dashboard.activeAlerts.length > 0 && (
            <div className="bg-orange-50 rounded-xl border border-orange-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                <h3 className="font-semibold text-orange-800">Alertas Activas</h3>
              </div>

              <div className="space-y-2">
                {dashboard.activeAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`p-3 rounded-lg ${
                      alert.priority === 'high'
                        ? 'bg-red-100 text-red-800'
                        : alert.priority === 'medium'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    <p className="text-sm font-medium">{alert.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
